import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getFieldErrors, IFieldError } from "../helpers";
import { IRole } from "./rolesReducer";

export interface IRentalItemAttachmentSession {
    _id: string;
    rentalItemAttachmentId: string;
    creationTimeStamp: number;
    activationTimeStamp?: number;
    abortionTimeStamp?: number;
    completionTimeStamp?: number;
}

export interface IServiceResourceSession {
    _id: string;
    serviceResourceId: string;
    creationTimeStamp: number;
    activationTimeStamp?: number;
    abortionTimeStamp?: number;
    completionTimeStamp?: number;
    rentalItemsAttachmentsSessions: IRentalItemAttachmentSession[];
    activeRentalItemAttachmentSessionId?: string;
}

export interface IRentalItemSession {
    _id: string;
    rentalItemId: string;
    creationTimeStamp: number;
    activationTimeStamp?: number;
    abortionTimeStamp?: number;
    completionTimeStamp?: number;
    servicesResourcesSessions?: IServiceResourceSession[];
    activeServiceResourceSessionId?: string;
}

export interface IEmailTokenUser {
    firstName: string;
    lastName: string;
    preferredName?: string;
    nickName?: string;
}

export interface IAuthenticatorSecret {
    _id: string;
    userId: string;
    secret: string,
    qrCodeData: string,
    confirmedAt: number,
    createdAt: string,
    updatedAt: string
}

export interface IAuthLevelUser {
    _id: string;
    customerId?: string;
    role?: IRole;
    usesEmail2FA: boolean;
    usesAuthenticator2FA: boolean;
    authToken: string;
    email2FADone: boolean;
    authenticator2FADone: boolean;
}

export interface IUser extends IAuthLevelUser {
    specialRole?: string;
    about?: string;
    imgSrc?: string;
    firstName: string;
    lastName: string;
    otherName?: string;
    nickName?: string;
    preferredName?: string;
    phone?: string;
    email: string;
    emailVerifiedAt?: number;
    status: string;
    smeOwnerStatus: string;
    activeMembershipType?: string;

    authenticatorSecret?: IAuthenticatorSecret;

    rentalItemsSessions?: IRentalItemSession[];
}

export interface IUser4Export extends IUser {

}

class SignUpErrs {
    form: string | null = null;
    firstName: string | null = null;
    lastName: string | null = null;
    otherName: string | null = null;
    nickName: string | null = null;
    preferredName: string | null = null;
    dateOfBirth: string | null = null;
    gender: string | null = null;
    email: string | null = null;
    phone: string | null = null;
    mobile: string | null = null;
    password: string | null = null;
    usesEmail2FA: string | null = null;
    usesAuthenticator2FA: string | null = null;
}

class LoginErrs {
    form: string | null = null;
    email: string | null = null;
    password: string | null = null;
    errCode: string | null = null;
}

class RequestPasswordResetErrs {
    form: string | null = null;
    email: string | null = null;
}

class ResetPasswordErrs {
    form: string | null = null;
    newPassword: string | null = null;
    token: string | null = null;

}

class ReqEmailVerificationErrs {
    form: string | null = null;
    errCode: string | null = null;
    email: string | null = null;
}

class ReqEmailVerificationAgainErrs {
    form: string | null = null;
    errCode: string | null = null;
    userId: string | null = null;
}

class Validate2FAOTPErrs {
    form: string | null = null;
    otp: string | null = null;
    type: string | null = null;
    errCode: string | null = null;
}

export interface IAuthState {
    signingUp: boolean;
    signedUp: boolean;
    signUpErrs: SignUpErrs;
    loggingIn: boolean;
    loginErrs: LoginErrs;
    confirmingLogin: boolean;
    confirmLoginErr: string | null;
    emailing2FAOTP: boolean;
    emailed2FAOTP: boolean;
    email2FAOTPErr: string | null;
    validating2FAOTP: boolean;
    validated2FAOTP: boolean;
    validate2FAOTPErr: string | null;
    validate2FAOTPErrs: Validate2FAOTPErrs;
    user: IAuthLevelUser | IUser | null;
    realRole: IRole | null;
    authToken: string | null;
    requestingPasswordReset: boolean;
    requestedPasswordReset: boolean;
    requestPasswordResetErrs: RequestPasswordResetErrs;
    fetchingEmailTokenUser: Boolean;
    emailTokenUser: IEmailTokenUser | null;
    fetchEmailTokenUserErr: string | null;
    resettingPassword: boolean;
    resettedPassword: boolean;
    resetPasswordErrs: ResetPasswordErrs;
    verifyingEmail: boolean;
    verifiedEmail: boolean;
    verifyEmailErr: string | null;
    requestingEmailVerification: boolean;
    requestedEmailVerification: boolean;
    reqEmailVerificationErrs: ReqEmailVerificationErrs;
    requestingEmailVerificationAgain: boolean;
    requestedEmailVerificationAgain: boolean;
    reqEmailVerificationAgainErrs: ReqEmailVerificationAgainErrs;
    loggingOut: boolean;
    loggedOut: boolean;
    logoutErr: string | null;
}

const authToken = localStorage?.authToken ? localStorage.authToken : null;

const initialState = {
    signingUp: false,
    signedUp: false,
    signUpErrs: new SignUpErrs,
    loggingIn: false,
    loginErrs: new LoginErrs(),
    confirmingLogin: false,
    confirmLoginErr: null,
    emailing2FAOTP: false,
    emailed2FAOTP: false,
    email2FAOTPErr: null,
    validating2FAOTP: false,
    validated2FAOTP: false,
    validate2FAOTPErr: null,
    validate2FAOTPErrs: new Validate2FAOTPErrs(),
    user: null,
    realRole: null,
    authToken,
    requestingPasswordReset: false,
    requestedPasswordReset: false,
    requestPasswordResetErrs: new RequestPasswordResetErrs(),
    fetchingEmailTokenUser: false,
    emailTokenUser: null,
    fetchEmailTokenUserErr: null,
    resettingPassword: false,
    resettedPassword: false,
    resetPasswordErrs: new ResetPasswordErrs(),
    verifyingEmail: false,
    verifiedEmail: false,
    verifyEmailErr: null,
    requestingEmailVerification: false,
    requestedEmailVerification: false,
    reqEmailVerificationErrs: new ReqEmailVerificationErrs(),
    requestingEmailVerificationAgain: false,
    requestedEmailVerificationAgain: false,
    reqEmailVerificationAgainErrs: new ReqEmailVerificationAgainErrs(),
    loggingOut: false,
    loggedOut: false,
    logoutErr: null
} as IAuthState;

const authSlice: any = createSlice({
    name: "auth",
    initialState,
    reducers: {
        clearSignUpFieldErr: (state: IAuthState, action: PayloadAction<string>) => {
            state.signUpErrs[action.payload as keyof SignUpErrs] = null;
        },
        clearSignUpErrs: (state: IAuthState) => {
            state.signUpErrs = new SignUpErrs();
        },
        tryResetSignUpFormErr: (state: IAuthState) => {
            let shouldReset = true;

            for (let key in state.signUpErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.signUpErrs[key as keyof SignUpErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.signUpErrs.form = null;
            }
        },
        resetSignedUp: (state: IAuthState) => {
            state.signedUp = false;
        },
        signUpStart: (state: IAuthState) => {
            state.signedUp = false;
            state.signUpErrs = new SignUpErrs();
            state.signingUp = true;
        },
        signUpPass: (state: IAuthState) => {
            state.signedUp = true;
            state.signingUp = false;
        },
        signUpFail: (state: IAuthState, action: PayloadAction<IFieldError[]>) => {
            state.signingUp = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.signUpErrs) {
                    if (err.field === key) {
                        state.signUpErrs[key as keyof SignUpErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.signUpErrs.form) {
                state.signUpErrs.form = 'Form incorrectly filled.';
            }
        },
        clearVerifyEmailErr: (state: IAuthState) => {
            state.verifyEmailErr = null;
        },
        resetVerifiedEmail: (state: IAuthState) => {
            state.verifiedEmail = false;
        },
        verifyEmailStart: (state: IAuthState) => {
            state.verifiedEmail = false;
            state.verifyEmailErr = null;
            state.verifyingEmail = true;
        },
        verifyEmailPass: (state: IAuthState) => {
            state.verifiedEmail = true;
            state.verifyingEmail = false;
        },
        verifyEmailFail: (state: IAuthState, action: PayloadAction<string>) => {
            state.verifyEmailErr = action.payload;
            state.verifyingEmail = false;
        },
        clearReqEmailVerificationFieldErr: (state: IAuthState, action: PayloadAction<string>) => {
            state.reqEmailVerificationErrs[action.payload as keyof ReqEmailVerificationErrs] = null;
        },
        clearReqEmailVerificationErrs: (state: IAuthState) => {
            state.reqEmailVerificationErrs = new ReqEmailVerificationErrs();
        },
        tryResetReqEmailVerificationFormErr: (state: IAuthState) => {
            let shouldReset = true;

            for (let key in state.reqEmailVerificationErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.reqEmailVerificationErrs[key as keyof ReqEmailVerificationErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.reqEmailVerificationErrs.form = null;
            }
        },
        resetRequestedEmailVerification: (state: IAuthState) => {
            state.requestedEmailVerification = false;
        },
        reqEmailVerificationStart: (state: IAuthState) => {
            state.reqEmailVerificationErrs = new ReqEmailVerificationErrs();
            state.requestedEmailVerification = false;
            state.requestingEmailVerification = true;
        },
        reqEmailVerificationPass: (state: IAuthState) => {
            state.requestingEmailVerification = false;
            state.requestedEmailVerification = true;

            state.loginErrs.errCode = null;
        },
        reqEmailVerificationFail: (state: IAuthState, action: PayloadAction<{ errs: IFieldError[]; errCode?: string; }>) => {
            state.requestingEmailVerification = false;

            const { errs, errCode } = action.payload;

            if (errCode) {
                state.reqEmailVerificationErrs.errCode = errCode;
            }

            for (const err of errs) {
                for (const key in state.reqEmailVerificationErrs) {
                    if (err.field === key) {
                        state.reqEmailVerificationErrs[key as keyof ReqEmailVerificationErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.reqEmailVerificationErrs.form) {
                state.reqEmailVerificationErrs.form = 'Form incorrectly filled.';
            }
        },

        clearReqEmailVerificationAgainFieldErr: (state: IAuthState, action: PayloadAction<string>) => {
            state.reqEmailVerificationAgainErrs[action.payload as keyof ReqEmailVerificationAgainErrs] = null;
        },
        clearReqEmailVerificationAgainErrs: (state: IAuthState) => {
            state.reqEmailVerificationAgainErrs = new ReqEmailVerificationAgainErrs();
        },
        tryResetReqEmailVerificationAgainFormErr: (state: IAuthState) => {
            let shouldReset = true;

            for (let key in state.reqEmailVerificationAgainErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.reqEmailVerificationAgainErrs[key as keyof ReqEmailVerificationAgainErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.reqEmailVerificationAgainErrs.form = null;
            }
        },
        resetRequestedEmailVerificationAgain: (state: IAuthState) => {
            state.requestedEmailVerificationAgain = false;
        },
        reqEmailVerificationAgainStart: (state: IAuthState) => {
            state.reqEmailVerificationAgainErrs = new ReqEmailVerificationAgainErrs();
            state.requestedEmailVerificationAgain = false;
            state.requestingEmailVerificationAgain = true;
        },
        reqEmailVerificationAgainPass: (state: IAuthState) => {
            state.requestingEmailVerificationAgain = false;
            state.requestedEmailVerificationAgain = true;

            state.loginErrs.errCode = null;
        },
        reqEmailVerificationAgainFail: (state: IAuthState, action: PayloadAction<{ errs: IFieldError[]; errCode?: string; }>) => {
            state.requestingEmailVerificationAgain = false;

            const { errs, errCode } = action.payload;

            if (errCode) {
                state.reqEmailVerificationAgainErrs.errCode = errCode;
            }

            for (const err of errs) {
                for (const key in state.reqEmailVerificationAgainErrs) {
                    if (err.field === key) {
                        state.reqEmailVerificationAgainErrs[key as keyof ReqEmailVerificationAgainErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.reqEmailVerificationAgainErrs.form) {
                state.reqEmailVerificationAgainErrs.form = 'Form incorrectly filled.';
            }
        },

        clearLoginFieldErr: (state: IAuthState, action: PayloadAction<string>) => {
            state.loginErrs[action.payload as keyof LoginErrs] = null;
        },
        clearLoginErrs: (state: IAuthState) => {
            state.loginErrs = new LoginErrs();
            state.loginErrs.errCode = null;
        },
        tryResetLoginFormErr: (state: IAuthState) => {
            let shouldReset = true;

            for (let key in state.loginErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.loginErrs[key as keyof LoginErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.loginErrs.form = null;
            }
        },
        loginStart: (state: IAuthState) => {
            state.loggingIn = true;
            state.loginErrs = new LoginErrs();
        },
        loginPass: (state: IAuthState, action: PayloadAction<IUser | IAuthLevelUser>) => {
            state.loggingIn = false;
            state.user = action.payload;
            state.authToken = action.payload.authToken as string;
            localStorage.authToken = action.payload.authToken;
        },
        loginFail: (state: IAuthState, action: PayloadAction<{ errs: IFieldError[]; errCode?: string; }>) => {
            state.loggingIn = false;

            const { errs, errCode } = action.payload;

            if (errCode) {
                state.loginErrs.errCode = errCode;
            }

            for (const err of errs) {
                for (const key in state.loginErrs) {
                    if (err.field === key) {
                        state.loginErrs[key as keyof LoginErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.loginErrs.form) {
                state.loginErrs.form = 'Form incorrectly filled.';
            }
        },
        resetConfirmLoginErr: (state: IAuthState) => {
            state.confirmLoginErr = null;
        },
        confirmLoginStart: (state: IAuthState) => {
            state.confirmingLogin = true;
        },
        confirmLoginPass: (state: IAuthState, action: PayloadAction<IUser | IAuthLevelUser>) => {
            state.confirmingLogin = false;
            state.user = action.payload;
            state.realRole = action.payload?.role ?? null;
        },
        confirmLoginFail: (state: IAuthState, action: PayloadAction<string>) => {
            state.user = null;
            state.realRole = null;
            state.authToken = null;
            localStorage.clear();

            state.confirmLoginErr = action.payload;
            state.confirmingLogin = false;
        },
        clearValidate2FAOTPFieldErr: (state: IAuthState, action: PayloadAction<string>) => {
            state.validate2FAOTPErrs[action.payload as keyof Validate2FAOTPErrs] = null;
        },
        clearValidate2FAOTPErrs: (state: IAuthState) => {
            state.validate2FAOTPErrs = new Validate2FAOTPErrs();
            state.validate2FAOTPErrs.errCode = null;
        },
        tryResetValidate2FAOTPFormErr: (state: IAuthState) => {
            let shouldReset = true;

            for (let key in state.validate2FAOTPErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.validate2FAOTPErrs[key as keyof Validate2FAOTPErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.validate2FAOTPErrs.form = null;
            }
        },
        clearValidate2FAOTPErr: (state: IAuthState) => {
            state.validate2FAOTPErr = null;
        },
        resetValidated2FAOTP: (state: IAuthState) => {
            state.validated2FAOTP = false;
        },
        validate2FAOTPStart: (state: IAuthState) => {
            state.validate2FAOTPErrs = new Validate2FAOTPErrs();
            state.validate2FAOTPErr = null;
            state.validated2FAOTP = false;
            state.validating2FAOTP = true;
        },
        validate2FAOTPPass: (state: IAuthState, action: PayloadAction<IAuthLevelUser>) => {
            state.user = action.payload;
            state.authToken = action.payload.authToken;
            localStorage.authToken = action.payload.authToken;
            state.validated2FAOTP = true;
            state.validating2FAOTP = false;
        },
        validate2FAOTPFail: (state: IAuthState, action: PayloadAction<{ errs: IFieldError[], errCode?: string }>) => {
            state.validating2FAOTP = false;

            const { errs, errCode } = action.payload;

            if (errCode) {
                state.validate2FAOTPErrs.errCode = errCode
            }

            for (const err of errs) {
                for (const key in state.validate2FAOTPErrs) {
                    if (err.field === key) {
                        state.validate2FAOTPErrs[key as keyof Validate2FAOTPErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.validate2FAOTPErrs.form) {
                state.validate2FAOTPErrs.form = 'Form incorrectly filled.';
            }
        },
        clearEmail2FAOTPErr: (state: IAuthState) => {
            state.email2FAOTPErr = null;
        },
        resetEmailed2FAOTP: (state: IAuthState) => {
            state.emailed2FAOTP = false;
        },
        email2FAOTPStart: (state: IAuthState) => {
            state.email2FAOTPErr = null;
            state.emailed2FAOTP = false;
            state.emailing2FAOTP = true;
        },
        email2FAOTPPass: (state: IAuthState) => {
            state.emailed2FAOTP = true;
            state.emailing2FAOTP = false;
        },
        email2FAOTPFail: (state: IAuthState, action: PayloadAction<string>) => {
            state.email2FAOTPErr = action.payload;
            state.emailing2FAOTP = false;
        },
        clearRequestPasswordResetFieldErr: (state: IAuthState, action: PayloadAction<string>) => {
            state.requestPasswordResetErrs[action.payload as keyof RequestPasswordResetErrs] = null;
        },
        clearRequestPasswordResetErrs: (state: IAuthState) => {
            state.requestPasswordResetErrs = new RequestPasswordResetErrs();
        },
        tryResetRequestPasswordResetFormErr: (state: IAuthState) => {
            let shouldReset = true;

            for (let key in state.requestPasswordResetErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.requestPasswordResetErrs[key as keyof RequestPasswordResetErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.requestPasswordResetErrs.form = null;
            }
        },
        resetRequestedPasswordReset: (state: IAuthState) => {
            state.requestedPasswordReset = false;
        },
        requestPasswordResetStart: (state: IAuthState) => {
            state.requestedPasswordReset = false;
            state.requestPasswordResetErrs = new RequestPasswordResetErrs();
            state.requestingPasswordReset = true;
        },
        requestPasswordResetPass: (state: IAuthState) => {
            state.requestedPasswordReset = true;
            state.requestingPasswordReset = false;
        },
        requestPasswordResetFail: (state: IAuthState, action: PayloadAction<IFieldError[]>) => {
            state.requestingPasswordReset = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.requestPasswordResetErrs) {
                    if (err.field === key) {
                        state.requestPasswordResetErrs[key as keyof RequestPasswordResetErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.requestPasswordResetErrs.form) {
                state.requestPasswordResetErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchEmailTokenUserErr: (state: IAuthState) => {
            state.fetchEmailTokenUserErr = null;
        },
        clearEmailTokenUser: (state: IAuthState) => {
            state.emailTokenUser = null;
        },
        fetchEmailTokenUserStart: (state: IAuthState) => {
            state.fetchEmailTokenUserErr = null;
            state.emailTokenUser = null;
            state.fetchingEmailTokenUser = true;
        },
        fetchEmailTokenUserPass: (state: IAuthState, action: PayloadAction<IEmailTokenUser>) => {
            state.emailTokenUser = action.payload;
            state.fetchingEmailTokenUser = false;
        },
        fetchEmailTokenUserFail: (state: IAuthState, action: PayloadAction<string>) => {
            state.fetchEmailTokenUserErr = action.payload;
            state.fetchingEmailTokenUser = false;
        },
        clearResetPasswordFieldErr: (state: IAuthState, action: PayloadAction<string>) => {
            state.resetPasswordErrs[action.payload as keyof ResetPasswordErrs] = null;
        },
        clearResetPasswordErrs: (state: IAuthState) => {
            state.resetPasswordErrs = new ResetPasswordErrs();
        },
        tryResetResetPasswordFormErr: (state: IAuthState) => {
            let shouldReset = true;

            for (let key in state.resetPasswordErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.resetPasswordErrs[key as keyof ResetPasswordErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.resetPasswordErrs.form = null;
            }
        },
        resetResettedPassword: (state: IAuthState) => {
            state.resettedPassword = false;
        },
        resetPasswordStart: (state: IAuthState) => {
            state.resettedPassword = false;
            state.resetPasswordErrs = new ResetPasswordErrs();
            state.resettingPassword = true;
        },
        resetPasswordPass: (state: IAuthState) => {
            state.resettedPassword = true;
            state.resettingPassword = false;
        },
        resetPasswordFail: (state: IAuthState, action: PayloadAction<IFieldError[]>) => {
            state.resettingPassword = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.resetPasswordErrs) {
                    if (err.field === key) {
                        state.resetPasswordErrs[key as keyof ResetPasswordErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.resetPasswordErrs.form) {
                state.resetPasswordErrs.form = 'Form incorrectly filled.';
            }
        },
        clearLogoutErr: (state: IAuthState) => {
            state.logoutErr = null;
        },
        resetLoggedOut: (state: IAuthState) => {
            state.loggedOut = false;
        },
        logoutStart: (state: IAuthState) => {
            state.logoutErr = null;
            state.loggedOut = false;
            state.loggingOut = true;
        },
        logoutPass: (state: IAuthState) => {
            //undo everything done in "login"
            state.user = null;
            state.realRole = null;
            state.authToken = null;
            localStorage.authToken = null;

            state.loggedOut = true;
            state.loggingOut = false;
        },
        logoutFail: (state: IAuthState, action: PayloadAction<string>) => {
            state.logoutErr = action.payload;
            state.loggingOut = false;
        },
        setAuthUserRole: (state: IAuthState, action: PayloadAction<IRole>) => {
            if (state.user) {
                state.user.role = action.payload;
            }
        },
        resetAuthUserRole: (state: IAuthState) => {
            if (state.user && state.realRole) {
                state.user.role = state.realRole;
            }
        }
    }

});

export const {
    clearSignUpFieldErr,
    clearSignUpErrs,
    tryResetSignUpFormErr,
    resetSignedUp,
    signUpStart,
    signUpPass,
    signUpFail,
    clearReqEmailVerificationFieldErr,
    clearReqEmailVerificationErrs,
    tryResetReqEmailVerificationFormErr,
    resetRequestedEmailVerification,
    reqEmailVerificationStart,
    reqEmailVerificationPass,
    reqEmailVerificationFail,
    clearReqEmailVerificationAgainFieldErr,
    clearReqEmailVerificationAgainErrs,
    tryResetReqEmailVerificationAgainFormErr,
    resetRequestedEmailVerificationAgain,
    reqEmailVerificationAgainStart,
    reqEmailVerificationAgainPass,
    reqEmailVerificationAgainFail,
    clearVerifyEmailErr,
    resetVerifiedEmail,
    verifyEmailStart,
    verifyEmailPass,
    verifyEmailFail,
    clearLoginFieldErr,
    clearLoginErrs,
    tryResetLoginFormErr,
    loginStart,
    loginPass,
    loginFail,
    resetConfirmLoginErr,
    confirmLoginStart,
    confirmLoginPass,
    confirmLoginFail,
    clearEmail2FAOTPErr,
    resetEmailed2FAOTP,
    email2FAOTPStart,
    email2FAOTPPass,
    email2FAOTPFail,
    clearValidate2FAOTPFieldErr,
    clearValidate2FAOTPErrs,
    tryResetValidate2FAOTPFormErr,
    clearValidate2FAOTPErr,
    resetValidated2FAOTP,
    validate2FAOTPStart,
    validate2FAOTPPass,
    validate2FAOTPFail,
    clearRequestPasswordResetFieldErr,
    clearRequestPasswordResetErrs,
    tryResetRequestPasswordResetFormErr,
    resetRequestedPasswordReset,
    requestPasswordResetStart,
    requestPasswordResetPass,
    requestPasswordResetFail,
    clearFetchEmailTokenUserErr,
    clearEmailTokenUser,
    fetchEmailTokenUserStart,
    fetchEmailTokenUserPass,
    fetchEmailTokenUserFail,
    clearResetPasswordFieldErr,
    clearResetPasswordErrs,
    tryResetResetPasswordFormErr,
    resetResettedPassword,
    resetPasswordStart,
    resetPasswordPass,
    resetPasswordFail,
    clearLogoutErr,
    resetLoggedOut,
    logoutStart,
    logoutPass,
    logoutFail,
    setAuthUserRole,
    resetAuthUserRole
} = authSlice.actions;

interface ITrySignUpArg {
    data: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        password: string;

        otherName?: string;
        nickName?: string;
        preferredName?: string;
        dateOfBirth?: string;
        gender?: string;
        mobile?: string;
        usesEmail2FA?: string;
        usesAuthenticator2FA?: string;
    }
}


export const trySignUp = createAsyncThunk("auth/trySignUp", (arg: ITrySignUpArg, { dispatch }) => {
    const { data } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/sign-up`;

    dispatch(signUpStart());
    axios.post(endpoint, data, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(signUpPass());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(signUpFail(fieldErrors));
    });
});

interface ITryVerifyEmail {
    emailToken: string;
}

export const tryVerifyEmail = createAsyncThunk("auth/tryVerifyEmail", (arg: ITryVerifyEmail, { dispatch }) => {
    const { emailToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/email-verification-tokens/${emailToken}/verify`;

    dispatch(verifyEmailStart());
    setTimeout(() => {
        axios.post(endpoint, {}, {
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => {
            dispatch(verifyEmailPass());
        }).catch(err => {
            const data = err?.response?.data;
            let error = '';

            if (data?.errMsg) {
                error = data.errMsg;
            } else if (data?.errors) {
                error = getFieldErrors(data.errors)[0].msg;
            } else {
                error = 'Check your network connection.';
            }

            dispatch(verifyEmailFail(error));
        });
    }, 1000);
});

interface ITryReqEmailVerificationArg {
    email: string;
}

export const tryReqEmailVerification = createAsyncThunk("auth/tryReqEmailVerification", (arg: ITryReqEmailVerificationArg, { dispatch }) => {
    const { email } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/email-user-2-verify-email`;

    dispatch(reqEmailVerificationStart());

    axios.post(endpoint, { email }, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(reqEmailVerificationPass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];
        let errCode: string | undefined = undefined;

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];
            if (data?.errCode) {
                errCode = data.errCode;
            }

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(reqEmailVerificationFail({ errs: fieldErrors, errCode }));
    });
});

interface ITryReqEmailVerificationAgainArg {
    authToken: string;
    userId: string;
}

export const tryReqEmailVerificationAgain = createAsyncThunk("auth/tryReqEmailVerificationAgain", (arg: ITryReqEmailVerificationAgainArg, { dispatch }) => {
    const { userId, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/email-user-2-verify-email-again`;

    dispatch(reqEmailVerificationAgainStart());

    axios.post(endpoint, { userId }, {
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${authToken}`,
        }
    }).then(res => {
        dispatch(reqEmailVerificationAgainPass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];
        let errCode: string | undefined = undefined;

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];
            if (data?.errCode) {
                errCode = data.errCode;
            }

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(reqEmailVerificationAgainFail({ errs: fieldErrors, errCode }));
    });
});

interface ITryLoginArg {
    email: string;
    password: string;
}

export const tryLogin = createAsyncThunk("auth/tryLogin", (arg: ITryLoginArg, { dispatch }) => {
    const { email, password } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/login`;

    dispatch(loginStart());

    axios.post(endpoint, { email, password }, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(loginPass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];
        let errCode: string | undefined = undefined;

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];
            if (data?.errCode) {
                errCode = data.errCode;
            }

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(loginFail({ errs: fieldErrors, errCode }));
    });
});


interface IConfirmLoginArg {
    authToken: string;
    setFavouriteRentalItems?: (favItems: any[]) => void;
}

export const tryConfirmLogin = createAsyncThunk('auth/tryConfirmLogin', async (arg: IConfirmLoginArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/auth/user`;

    dispatch(confirmLoginStart());
    axios.get(endpoint, {
        headers: {
            'Authorization': `Bearer ${arg.authToken}`,
            'Content-Type': 'application/json'
        }
    }).then(res => {
        if (arg?.setFavouriteRentalItems) {
            arg.setFavouriteRentalItems(res.data?.favouriteRentalItems);
        }
        dispatch(confirmLoginPass(res.data));
    }).catch(err => {
        const data = err.response?.data;

        if (data && data?.errMsg) {
            dispatch(confirmLoginFail(data.errMsg));
        } else {
            dispatch(confirmLoginFail('Check your internet connection.'));
        }
    });
});

interface ITryEmail2FAOTPArg {
    authToken: string;
}

export const tryEmail2FAOTP = createAsyncThunk("auth/tryEmail2FAOTP", (arg: ITryEmail2FAOTPArg, { dispatch }) => {
    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/email-2fa-otp`;

    dispatch(email2FAOTPStart());
    axios.post(endpoint, {}, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(email2FAOTPPass());
    }).catch(err => {
        const data = err?.response?.data;
        let error = '';

        if (data?.errMsg) {
            error = data.errMsg;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(email2FAOTPFail(error));
    });
});

interface ITryValidate2FAOTPArg {
    otp: string;
    type: string;
    authToken: string;
}

export const tryValidate2FAOTP = createAsyncThunk("auth/tryValidate2FAOTP", (arg: ITryValidate2FAOTPArg, { dispatch }) => {
    const { otp, type, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/validate-2fa-otp`;

    dispatch(validate2FAOTPStart());
    axios.post(endpoint, { otp, type }, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(validate2FAOTPPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];
        let errCode: string | undefined = undefined;

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];
            if (data?.errCode) {
                errCode = data.errCode;
            }

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(validate2FAOTPFail({ errs: fieldErrors, errCode }));
    });
});

interface ITryRequestPasswordResetArg {
    email: string;
}

export const tryRequestPasswordReset = createAsyncThunk("auth/tryRequestPasswordReset", (arg: ITryRequestPasswordResetArg, { dispatch }) => {
    const { email } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/reset-password-request`;

    dispatch(requestPasswordResetStart());
    axios.post(endpoint, { email }, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(requestPasswordResetPass());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(requestPasswordResetFail(fieldErrors));
    });
});

interface ITryFetchEmailTokenUserArg {
    emailToken: string;
}

export const tryFetchEmailTokenUser = createAsyncThunk("auth/tryFetchEmailTokenUser", (arg: ITryFetchEmailTokenUserArg, { dispatch }) => {
    const { emailToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/email-tokens/${emailToken}/user`;

    dispatch(fetchEmailTokenUserStart());
    axios.get(endpoint, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(fetchEmailTokenUserPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;
        let error = '';

        if (data?.errMsg) {
            error = data.errMsg;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(fetchEmailTokenUserFail(error));
    });

});

interface ITryResetPasswordArg {
    newPassword: string;
    token: string;
}

export const tryResetPassword = createAsyncThunk("auth/tryResetPassword", (arg: ITryResetPasswordArg, { dispatch }) => {
    const { newPassword, token } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/reset-password`;

    dispatch(resetPasswordStart());
    axios.post(endpoint, { newPassword, token }, {
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        dispatch(resetPasswordPass());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(resetPasswordFail(fieldErrors));
    });
});

interface ITryLogoutArg {
    authToken: string;
}

export const tryLogout = createAsyncThunk("auth/logout", (arg: ITryLogoutArg, { dispatch }) => {

    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/logout`;

    dispatch(logoutStart());
    axios.post(endpoint, {}, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(logoutPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;
        let error = '';

        if (data?.errMsg) {
            error = data.errMsg;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(logoutFail(error));
    });

});

export default authSlice.reducer;